import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { tr } from 'date-fns/locale';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Tooltip, Legend, Filler);

const PERIODS = [
  { id: 'daily', label: 'Günlük' },
  { id: 'weekly', label: 'Haftalık' },
  { id: 'monthly', label: 'Aylık' }
];

const periodLabel = (row, period) => {
  const start = parseISO(row.period_start);
  if (period === 'monthly') return format(start, 'MMMM yyyy', { locale: tr });
  if (period === 'weekly') {
    const end = parseISO(row.period_end);
    return `${format(start, 'd MMM', { locale: tr })} - ${format(end, 'd MMM', { locale: tr })}`;
  }
  return format(start, 'd MMM', { locale: tr });
};

const OverviewTab = ({ showToast }) => {
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [period, setPeriod] = useState('daily');
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchOverview = useCallback(async (p, isInitial) => {
    if (isInitial) setLoading(true); else setHistoryLoading(true);
    try {
      const res = await axios.get(`/api/meta/overview?period=${p}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('crm_token')}` }
      });
      setStats(res.data.stats || {});
      setHistory(res.data.history || []);
    } catch (err) {
      console.error(err);
      showToast('Veriler yüklenirken hata oluştu', 'error');
    } finally {
      setLoading(false);
      setHistoryLoading(false);
    }
  }, [showToast]);

  useEffect(() => { fetchOverview(period, true); /* eslint-disable-next-line */ }, []);

  const changePeriod = (p) => {
    setPeriod(p);
    fetchOverview(p, false);
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>Yükleniyor...</div>;
  if (!stats) return <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>Veri bulunamadı. Lütfen sayfayı yenileyin.</div>;

  const kpis = [
    { label: 'Toplam Harcama', value: `₺${parseFloat(stats.total_spend || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`, icon: '💰', variant: 'primary' },
    { label: 'Erişim', value: parseInt(stats.total_reach || 0).toLocaleString('tr-TR'), icon: '👁️', variant: 'success' },
    { label: 'Tıklama', value: parseInt(stats.total_clicks || 0).toLocaleString('tr-TR'), icon: '🖱️', variant: 'warning' },
    { label: 'Lead Sayısı', value: parseInt(stats.total_leads || 0).toLocaleString('tr-TR'), icon: '🎯', variant: 'primary' },
    { label: 'Mesaj Sayısı', value: parseInt(stats.total_messages || 0).toLocaleString('tr-TR'), icon: '💬', variant: 'success' },
    { label: 'CPL (Lead Maliyeti)', value: `₺${stats.total_leads > 0 ? (stats.total_spend / stats.total_leads).toLocaleString('tr-TR', { maximumFractionDigits: 2 }) : '0.00'}`, icon: '📉', variant: 'danger' },
  ];

  const labels = history.map(row => periodLabel(row, period));

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Harcama (₺)',
        data: history.map(row => parseFloat(row.spend || 0)),
        borderColor: '#00f2fe',
        backgroundColor: 'rgba(0, 242, 254, 0.12)',
        fill: true,
        tension: 0.3,
        yAxisID: 'y',
        pointRadius: 3
      },
      {
        label: 'Lead',
        data: history.map(row => parseInt(row.leads || 0, 10)),
        borderColor: '#8b5cf6',
        backgroundColor: 'rgba(139, 92, 246, 0.12)',
        fill: false,
        tension: 0.3,
        yAxisID: 'y1',
        pointRadius: 3
      },
      {
        label: 'Mesaj',
        data: history.map(row => parseInt(row.messages || 0, 10)),
        borderColor: '#10b981',
        backgroundColor: 'rgba(16, 185, 129, 0.12)',
        fill: false,
        tension: 0.3,
        yAxisID: 'y1',
        pointRadius: 3
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top', labels: { color: '#f3f4f6', font: { family: 'Plus Jakarta Sans', weight: 500 } } },
      tooltip: { enabled: true }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: '#9ca3af', font: { family: 'Plus Jakarta Sans', size: 11 } }
      },
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        grid: { color: 'rgba(255, 255, 255, 0.05)' },
        ticks: { color: '#00c2ff', font: { family: 'Plus Jakarta Sans' } },
        title: { display: true, text: '₺ Harcama', color: '#00c2ff', font: { family: 'Plus Jakarta Sans', size: 11 } }
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        beginAtZero: true,
        grid: { drawOnChartArea: false },
        ticks: { color: '#9ca3af', font: { family: 'Plus Jakarta Sans' }, precision: 0 },
        title: { display: true, text: 'Adet', color: '#9ca3af', font: { family: 'Plus Jakarta Sans', size: 11 } }
      }
    }
  };

  return (
    <div>
      <div className="stats-grid">
        {kpis.map((kpi, i) => (
          <div key={i} className="stat-card">
            <div className={`stat-icon stat-icon-${kpi.variant}`}>{kpi.icon}</div>
            <div className="stat-content">
              <div className="stat-label">{kpi.label}</div>
              <div className="stat-value">{kpi.value}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <span className="card-title">Zaman Bazlı Analiz</span>
          <div className="view-mode-toggle" style={{ display: 'flex', background: 'rgba(255, 255, 255, 0.03)', padding: '4px', borderRadius: '8px', border: '1px solid var(--border)', height: '36px', alignItems: 'center' }}>
            {PERIODS.map(p => (
              <button
                key={p.id}
                className={`btn btn-sm ${period === p.id ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => changePeriod(p.id)}
                style={{ padding: '4px 14px', borderRadius: '6px', fontSize: '12px', height: '28px', lineHeight: 1 }}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {historyLoading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>Yükleniyor...</div>
        ) : history.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
            Bu döneme ait geçmiş veri henüz yok. "Bağlantı" sekmesinden senkronize edin, ilk senkronizasyondan sonra geçmiş burada birikmeye başlar.
          </div>
        ) : (
          <>
            <div style={{ height: '320px', padding: '12px 4px 4px' }}>
              <Line data={chartData} options={chartOptions} />
            </div>

            <div className="table-container" style={{ border: 'none', borderTop: '1px solid var(--border)', borderRadius: 0, marginTop: '16px' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Dönem</th>
                    <th>Harcama</th>
                    <th>Gösterim</th>
                    <th>Tıklama</th>
                    <th>Lead</th>
                    <th>Mesaj</th>
                  </tr>
                </thead>
                <tbody>
                  {[...history].reverse().map((row, i) => (
                    <tr key={i}>
                      <td>{periodLabel(row, period)}</td>
                      <td>₺{parseFloat(row.spend || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</td>
                      <td>{parseInt(row.impressions || 0, 10).toLocaleString('tr-TR')}</td>
                      <td>{parseInt(row.clicks || 0, 10).toLocaleString('tr-TR')}</td>
                      <td style={{ color: 'var(--secondary)', fontWeight: '500' }}>{row.leads || 0}</td>
                      <td style={{ color: '#10b981', fontWeight: '500' }}>{row.messages || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title">Özet Bilgi</span>
        </div>
        <p style={{ color: 'var(--text-muted)', margin: 0 }}>
          Yukarıdaki toplamlar bağlamış olduğunuz Meta reklam hesabının sistemde kayıtlı son 30 günlük özet metrikleridir. Zaman bazlı grafik, her senkronizasyonda güncellenen gerçek gün-bazlı geçmişi gösterir. Daha fazla analiz için Kampanyalar sekmesini kullanabilirsiniz.
        </p>
      </div>
    </div>
  );
};

export default OverviewTab;
